// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import { useEffect, useState } from 'react';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function GoogleIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.43.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.93l3.66-2.83z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );
}

function InnerButton({ onSuccess, disabled }) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);

  // useGoogleLogin with flow=auth-code returns a 'code' that we exchange server-side
  const start = useGoogleLogin({
    flow: 'auth-code',
    // Use window.location.origin (NOT hardcoded). Backend expects same redirect_uri.
    redirect_uri: window.location.origin,
    onSuccess: async (resp) => {
      try {
        setLoading(true);
        const apiResp = await axios.post(`${API}/auth/google`, {
          code: resp.code,
          redirect_uri: window.location.origin,
        });
        const { token, user } = apiResp.data;
        localStorage.setItem('ddns-token', token);
        if (onSuccess) onSuccess(user);
        toast.success('authenticated');
        // Hard reload so AuthContext re-initializes with the new token from localStorage
        const target = user.role === 'admin' ? '/admin' : '/dashboard';
        window.location.replace(target);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'google sign-in failed');
        setLoading(false);
      }
    },
    onError: () => {
      toast.error('google sign-in cancelled');
      setLoading(false);
    },
  });

  return (
    <Button
      type="button"
      variant="outline"
      onClick={() => { setLoading(true); start(); }}
      disabled={disabled || loading}
      data-testid="google-signin-btn"
      className="w-full h-10 rounded text-[11px] tracking-wider gap-2 border-border/50 bg-card hover:bg-card/80"
    >
      {loading
        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
        : (<><GoogleIcon /> {t('auth.google_login')}</>)}
    </Button>
  );
}

export default function GoogleSignInButton() {
  const { t } = useLanguage();
  const [config, setConfig] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/auth/google/config`).then(res => {
      if (!cancelled) setConfig(res.data);
    }).catch(() => {
      if (!cancelled) setConfig({ enabled: false, client_id: '' });
    });
    return () => { cancelled = true; };
  }, []);

  if (!config || !config.enabled || !config.client_id) return null;

  return (
    <div className="space-y-3" data-testid="google-signin-wrapper">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-px bg-border/40" />
        <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-muted-foreground">
          {t('auth.or_separator')}
        </span>
        <div className="flex-1 h-px bg-border/40" />
      </div>
      <GoogleOAuthProvider clientId={config.client_id}>
        <InnerButton />
      </GoogleOAuthProvider>
    </div>
  );
}
